/* eslint-disable @typescript-eslint/no-explicit-any */
import { MenuItem, TextField } from "@mui/material";
import * as React from "react";
import { UserRoles } from "../../../types/auth";
import { camelCaseToHumanReadable } from "../../../helpers/utils";

interface IUserRoleSelectProps {
  label?: string;
  placeholder?: string;
  value?: UserRoles;
  onChange: (newVal?: UserRoles) => void;
  error?: boolean;
  helperText?: string;
  currentUserRole?: UserRoles; // Role of the user making the change
  isEditingOwnProfile?: boolean; // Whether editing own profile
}
export default function UserRoleSelect(props: IUserRoleSelectProps) {
  const { value, onChange, helperText, error, currentUserRole, isEditingOwnProfile } = props;
  
  // Get available roles based on current user's role
  const getAvailableRoles = (): UserRoles[] => {
    if (!currentUserRole) {
      // If no current user role provided, show all roles (for backward compatibility)
      return Object.values(UserRoles);
    }

    // If editing own profile
    if (isEditingOwnProfile) {
      // Regular users can't change their role at all (can only keep user role)
      if (currentUserRole === UserRoles.user) {
        return [UserRoles.user];
      }
      // Company admins can keep their role or downgrade to user, but can't upgrade
      if (currentUserRole === UserRoles.companyAdmin) {
        return [UserRoles.user, UserRoles.companyAdmin];
      }
      // Super admins can change to any role
      if (currentUserRole === UserRoles.superadmin) {
        return Object.values(UserRoles);
      }
      // Admin staff can keep their role or downgrade to user, but can't upgrade
      if (currentUserRole === UserRoles.adminStaff) {
        return [UserRoles.user, UserRoles.adminStaff];
      }
    }

    // When editing other users (not own profile):
    // Only super admin can set superadmin and adminStaff roles
    // Only Company Admin can set companyAdmin role
    // Regular users can't set any admin roles
    if (currentUserRole === UserRoles.superadmin) {
      return Object.values(UserRoles); // Super admin can set any role
    } else if (currentUserRole === UserRoles.companyAdmin) {
      // Company Admin can set user and companyAdmin, but not superadmin or adminStaff
      return [UserRoles.user, UserRoles.companyAdmin];
    } else {
      // Regular users can't set any admin roles
      return [UserRoles.user];
    }
  };

  const availableRoles = getAvailableRoles();

  return (
    <TextField
      label={props.label}
      select
      fullWidth
      placeholder="Select User Role"
      helperText={helperText}
      error={error}
      value={value || ""}
      size="small"
      onChange={(e) => {
        onChange(e.target.value as unknown as UserRoles);
      }}
      disabled={isEditingOwnProfile && currentUserRole === UserRoles.user}
    >
      {availableRoles.map((role, i) => (
        <MenuItem key={`comp-type-${i}`} value={role as UserRoles}>
          {camelCaseToHumanReadable(role)}
        </MenuItem>
      ))}
    </TextField>
  );
}
